import type { ReactNode } from 'react';
import { photoUrl } from '../lib/photos';

/**
 * Marks copy that is still a proposal so it is easy to find in code
 * (data-mock). It used to paint a yellow SAMPLE tag on screen; the family
 * asked for the tags to go (2026-09-08), so it now renders a plain wrapper.
 * Delete the wrapper when the real content lands; nothing else depends on it.
 */
export function Mock({
  children,
  className,
  inline,
}: {
  children: ReactNode;
  /** Kept so call sites need not change; no longer shown. */
  label?: string;
  className?: string;
  inline?: boolean;
}) {
  const Tag = inline ? 'span' : 'div';
  return (
    <Tag data-mock className={className}>
      {children}
    </Tag>
  );
}

export function Photo({ name, alt, className }: { name: string; alt: string; className?: string }) {
  const url = photoUrl(name);
  // A caller that positions the slot itself (the home hero passes `absolute
  // inset-0`) must not get `relative` as well: both utilities would apply and
  // `relative` wins the cascade, collapsing the slot to zero height.
  const position = /\babsolute\b/.test(className ?? '') ? '' : 'relative';
  return (
    <div className={`bg-stage bg-hex ${position} overflow-hidden ${className ?? ''}`}>
      {url ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <span className="pointer-events-none absolute bottom-2 right-2 font-sans text-[10px] uppercase tracking-wider text-ink-400">
          photos/{name}
        </span>
      )}
    </div>
  );
}
